import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * MyPayslipsCard — Staff self-serve view of their own payslips.
 *
 * Mounted on the Staff dashboard. Lists past payslips with one-tap
 * download. Uses `/api/payslips/me` (read-only, scoped to the caller's
 * user_id) and `/api/payslips/{id}/download` which the backend allows
 * for the owner staff member as well as admins.
 */
export function MyPayslipsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await api.listMyPayslips();
        if (!cancelled) setItems(rows || []);
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Could not load payslips');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const open = async (id) => {
    try {
      const url = await api.downloadPayslipPdfUrl(id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error(err.message || 'Download failed');
    }
  };

  return (
    <div className="card" style={{ marginTop: '1rem' }} data-testid="my-payslips-card">
      <h3 style={{ margin: '0 0 0.25rem 0' }}>💷 My Payslips</h3>
      <p style={{ margin: '0 0 0.75rem 0', color: '#6b7280', fontSize: '0.85rem' }}>
        Your recent payslips. Tap any row to open the branded PDF.
      </p>
      {loading ? (
        <div style={{ color: '#6b7280' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div
          style={{
            padding: '0.75rem 1rem',
            background: '#f9fafb',
            borderRadius: '8px',
            color: '#6b7280',
            fontSize: '0.85rem',
          }}
        >
          No payslips yet. Your manager will generate them at the end of each pay period.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                <th style={th}>Period</th>
                <th style={th}>Pay date</th>
                <th style={th}>Hours</th>
                <th style={th}>Net</th>
                <th style={th}>Status</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p._id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={td}>{p.period_start} → {p.period_end}</td>
                  <td style={td}>{p.pay_date}</td>
                  <td style={td}>{(p.hours_worked ?? 0).toFixed(2)}</td>
                  <td style={td}><strong>£{(p.net ?? 0).toFixed(2)}</strong></td>
                  <td style={td}><span style={pill(p.status)}>{p.status}</span></td>
                  <td style={td}>
                    <button
                      type="button"
                      onClick={() => open(p._id)}
                      data-testid={`my-payslip-download-${p._id}`}
                      style={{
                        padding: '0.3rem 0.7rem', background: '#00BCD4', color: '#fff',
                        border: 'none', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '0.78rem', fontWeight: 600,
                      }}
                    >
                      ⬇ Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { padding: '0.5rem', fontWeight: 700, fontSize: '0.78rem' };
const td = { padding: '0.5rem' };
const pill = (status) => ({
  padding: '0.15rem 0.5rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700,
  background: status === 'paid' ? '#dcfce7' : status === 'sent' ? '#dbeafe' : '#fef3c7',
  color:      status === 'paid' ? '#166534' : status === 'sent' ? '#1e40af' : '#92400e',
});

export default MyPayslipsCard;
